import React, { useState, useEffect, useRef } from 'react';

import CommonImage from '@components/CommonImage';

import { getInputValue, formatNumericValue, parseInputValue } from '../utils';

import DisplayCost from './DisplayCost';

const EmbroideryTable = ({
	data = [],
	totalCost = 0,
	onChange,
	isEditable = true,
	validationErrors = {}
}) => {
	const [embroideries, setEmbroideries] = useState([]);
	const isInitialMount = useRef(true);
	const prevDataRef = useRef(null);

	useEffect(() => {
		const dataString = JSON.stringify(data);
		const prevDataString = JSON.stringify(prevDataRef.current);

		if (dataString === prevDataString) {
			return;
		}

		if (data && Array.isArray(data) && data.length > 0) {
			const newEmbroideries = data.map((item, index) => ({
				id: `embroidery-${index}`,
				type: item.selected_embroidery_type || '',
				coverage: item.selected_coverage_area || '',
				cost: formatNumericValue(item.embroidery_price_usd)
			}));
			setEmbroideries(newEmbroideries);
		} else {
			setEmbroideries([
				{
					id: 'embroidery-0',
					type: '',
					coverage: '',
					cost: ''
				}
			]);
		}

		prevDataRef.current = data;
		if (isInitialMount.current) {
			isInitialMount.current = false;
		}
	}, [data]);

	const transformToApiFormat = (updatedEmbroideries) => {
		return updatedEmbroideries.map((item) => {
			const { type, coverage, cost } = item;
			return {
				embroidery_price_usd: formatNumericValue(cost),
				selected_embroidery_type: type || '',
				selected_coverage_area: coverage || ''
			};
		});
	};

	const notifyChange = (updatedEmbroideries) => {
		if (onChange && !isInitialMount.current) {
			onChange(transformToApiFormat(updatedEmbroideries));
		}
	};

	const handleAddEmbroidery = () => {
		const newEmbroidery = {
			id: `embroidery-${Date.now()}`,
			type: '',
			coverage: '',
			cost: ''
		};
		const updatedEmbroideries = [...embroideries, newEmbroidery];
		setEmbroideries(updatedEmbroideries);
		notifyChange(updatedEmbroideries);
	};

	const handleRemoveEmbroidery = (id) => {
		if (embroideries.length > 1) {
			const updatedEmbroideries = embroideries.filter((e) => e.id !== id);
			setEmbroideries(updatedEmbroideries);
			notifyChange(updatedEmbroideries);
		}
	};

	const handleInputChange = (embroideryId, field, value) => {
		const isTextField = field === 'type' || field === 'coverage';
		const updatedValue = parseInputValue(value, !isTextField);
		const updatedEmbroideries = embroideries.map((e) =>
			e.id === embroideryId ? { ...e, [field]: updatedValue } : e
		);
		setEmbroideries(updatedEmbroideries);
		notifyChange(updatedEmbroideries);
	};

	return (
		<div className="single-table costing-table-content mt-5">
			<div className="costing-table scroll-x-label">
				<table className="table no-hover">
					<thead>
						<tr>
							<th>Embroidery Cost Metrics</th>
							{embroideries.map((embroidery, index) => (
								<th key={embroidery.id}>
									<div className="d-flex align-items-center justify-content-between">
										<p>Embroidery {index + 1}</p>
										{embroideries.length > 1 && isEditable && (
											<CommonImage
												src="/icons/chip-close.svg"
												alt="remove"
												width={20}
												height={20}
												className="cursor-pointer"
												onClick={() => handleRemoveEmbroidery(embroidery.id)}
											/>
										)}
									</div>
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						<tr>
							<td>
								<p className="regular-12 gray_dark_02">Type</p>
							</td>
							{embroideries.map((embroidery, index) => {
								const errorKey = `embroidery-${index}-type`;
								const hasError = validationErrors[errorKey];
								return (
									<td key={embroidery.id}>
										<input
											type="text"
											value={embroidery.type || ''}
											onChange={(e) =>
												handleInputChange(embroidery.id, 'type', e.target.value)
											}
											readOnly={!isEditable}
											className={hasError ? 'common-border-error' : ''}
										/>
									</td>
								);
							})}
						</tr>

						<tr>
							<td>
								<p className="regular-12 gray_dark_02">Area Size</p>
							</td>
							{embroideries.map((embroidery, index) => {
								const errorKey = `embroidery-${index}-coverage`;
								const hasError = validationErrors[errorKey];
								return (
									<td key={embroidery.id}>
										<input
											type="text"
											value={embroidery.coverage || ''}
											onChange={(e) =>
												handleInputChange(embroidery.id, 'coverage', e.target.value)
											}
											readOnly={!isEditable}
											className={hasError ? 'common-border-error' : ''}
										/>
									</td>
								);
							})}
						</tr>

						<tr>
							<td>
								<p className="regular-12 gray_dark_02">Cost ($)</p>
							</td>
							{embroideries.map((embroidery) => (
								<td key={embroidery.id}>
									<input
										type="number"
										step="0.01"
										value={getInputValue(embroidery.cost)}
										onChange={(e) =>
											handleInputChange(embroidery.id, 'cost', e.target.value)
										}
										readOnly={!isEditable}
									/>
								</td>
							))}
						</tr>
						<tr className="cost-total-row">
							<td>
								<p className="semibold-14 gray_dark_01">Total Embroidery Cost</p>
							</td>
							<td colSpan={embroideries.length}>
								<p className="semibold-14 gray_dark_01">
									<DisplayCost totalCost={totalCost} />
								</p>
							</td>
						</tr>
					</tbody>
				</table>

				{isEditable && (
					<div className="add-more-button w-100">
						<button className="button gray-outline size32 w-100" onClick={handleAddEmbroidery}>
							<CommonImage src="/icons/plus-20.svg" alt="add" width={20} height={20} />
							<span className="ml-1 regular-14 gray_dark_01">Add Embroidery</span>
						</button>
					</div>
				)}
			</div>
		</div>
	);
};

export default EmbroideryTable;
